import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LogOut, User, Menu } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Get initials from display name (e.g., "J.R. Ewing" -> "JE")
const getInitials = (name: string): string => {
  const parts = name.split(' ').filter(p => p.length > 0);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  // First letter of first and last part
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

interface ChatHeaderProps {
  onMenuClick?: () => void;
  title?: string;
}

export const ChatHeader = ({ onMenuClick, title }: ChatHeaderProps) => {
  const { user, signOut } = useAuth();

  // Fetch user's display name
  const { data: profile } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('user_profiles')
        .select('display_name')
        .eq('user_id', user.id)
        .single();
      
      if (error) return null;
      return data;
    },
    enabled: !!user?.id,
  });

  const displayName = profile?.display_name || user?.email;

  return (
    <header className="shrink-0 border-b bg-background">
      <div className="flex h-12 items-center justify-between px-4">
        <div className="flex items-center gap-2"><Button variant="ghost" size="icon" className="h-11 w-11 md:hidden" aria-label="Open navigation" onClick={onMenuClick}><Menu className="h-5 w-5" /></Button><Link to="/new" className="font-display text-sm font-semibold md:hidden">SiliconSoap</Link>{title && <span className="text-sm font-semibold">{title}</span>}</div>
        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="min-h-11 min-w-11" aria-label="Account menu">
                <User className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled className="flex items-center gap-2">
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                    {getInitials(displayName || '')}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm truncate max-w-[180px] font-medium">
                  {displayName}
                </span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <Link to="/profile">
                <DropdownMenuItem>
                  <User className="h-4 w-4 mr-2" />
                  Profile
                </DropdownMenuItem>
              </Link>
              <DropdownMenuItem onClick={signOut}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Link to="/auth">
            <Button variant="outline" size="sm" className="min-h-11">
              Sign In
            </Button>
          </Link>
        )}
      </div>
    </header>
  );
};
